import compact from "lodash/compact";
import merge from "lodash/merge";
import { create as createEvent } from "./events";

const { google_container_id } = document.querySelector("body").dataset;
const nonce = document.querySelector('meta[name="csp-nonce"]')?.getAttribute("content");

const trackingIframeEnabled = window.RINSED_TRACKING_IFRAME === true;

// analytics.js + the Google Tag Manager plugin are ~72KB and only forward page/purchase events to
// GTM, which only happens when a google_container_id is present (customer-facing pages). Load them
// lazily so admin pages and the tracking-iframe path never ship GTM. The page/track calls below are
// fire-and-forget, so deferring the instance has no effect on user-facing callbacks.
let analyticsPromise;
const getAnalytics = () => {
  if (trackingIframeEnabled || !google_container_id) {
    return Promise.resolve(null);
  }
  analyticsPromise ||= Promise.all([
    import("analytics"),
    import("@analytics/google-tag-manager"),
  ]).then(([{ default: Analytics }, { default: googleTagManager }]) =>
    Analytics({
      app: "web",
      plugins: compact([googleTagManager({ containerId: google_container_id, nonce })]),
    })
  );
  return analyticsPromise;
};

const properties = () => {
  const parentUrl =
    removePrivateParams(document.querySelector('input[name="parent_url"]').value) || null;
  const referrer = document.querySelector('input[name="referrer"]').value || null;

  return {
    frame_url: location.href,
    parent_url: parentUrl,
    referrer: referrer,
    url: parentUrl,
  };
};

const removePrivateParams = (url) => {
  try {
    const urlObj = new URL(url);
    urlObj.searchParams.delete("email");
    urlObj.searchParams.delete("phone");
    urlObj.searchParams.delete("first_name");
    urlObj.searchParams.delete("last_name");
    urlObj.searchParams.delete("zip_code");
    return urlObj.toString();
  } catch (e) {
    return url;
  }
};

const adminProperties = () => {
  return {
    referrer: document.referrer,
  };
};

export const createRinsedEvent = ({ name, admin = false, payload = {} }) => {
  const props = admin ? adminProperties() : properties();
  const mergedProps = merge(props, payload);
  createEvent(name, mergedProps, { admin });
};

export const trackIframeLoad = (params, referrer) => {
  createRinsedEvent({ name: "Page loaded" });

  const payload = merge(properties(), {
    referrer,
    campaign: {
      adset: params.get("utm_adset"),
      content: params.get("utm_content"),
      medium: params.get("utm_medium"),
      name: params.get("utm_campaign"),
      source: params.get("utm_source") || params.get("referrer"),
      term: params.get("utm_term"),
    },
  });

  if (trackingIframeEnabled) {
    // page_view data is passed via URL params to the tracking iframe
  } else if (google_container_id) {
    getAnalytics().then((analytics) => analytics?.page(payload));
  }

  if (typeof gtag !== "undefined") {
    gtag("event", "page_view", { page_location: location.href });
  }
};

const compactPayload = (payload) => {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => {
      if (value === null || value === undefined) return false;
      if (typeof value === "string") return value !== "";
      if (typeof value === "object") return Object.keys(value).length > 0;
      return true;
    })
  );
};

export const trackLead = (payload, callback) => {
  const cleanPayload = compactPayload(payload);
  const { user_data: userData, ...eventPayload } = cleanPayload;
  const cleanUserData = compactPayload(userData || {});

  const detail = compactPayload({
    event: "generate_lead",
    ...eventPayload,
    user_data: cleanUserData,
  });
  window.dispatchEvent(new CustomEvent("tracking:event", { detail }));
  setTimeout(callback, 2000);
};

export const trackPurchase = (payload, serverSideGoogleAnalytics, callback) => {
  console.log("[Rinsed] tracking.js: trackPurchase called", {
    payload,
    serverSideGoogleAnalytics,
    trackingIframeEnabled,
  });

  if (!payload.transaction_id) {
    console.log("[Rinsed] tracking.js: no transaction_id, calling callback immediately");
    return callback();
  }

  // Only the tracking iframe uses the POS-oriented `item_id` (foreign_key, falling back to product
  // name). Every other sink below uses the internal product id for `item_id`, to preserve
  // continuity in our /events.json pipeline, Analytics.js/GTM, and direct gtag.
  const rawItems = payload.items || [];
  const iframeItems = rawItems.map(({ internal_item_id, ...item }) => item);
  const internalItems = rawItems.map(({ internal_item_id, ...item }) => ({
    ...item,
    item_id: internal_item_id,
  }));

  const internalPayload = { ...payload, items: internalItems };
  const mergedProperties = merge(properties(), internalPayload);
  console.log("[Rinsed] tracking.js: mergedProperties", mergedProperties);

  createEvent(
    "Purchase Completed",
    merge(mergedProperties, {
      gtag_present: typeof gtag !== "undefined",
      google_container_id_present: !!google_container_id,
    })
  );

  if (trackingIframeEnabled) {
    // Guides:
    //   - https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#send_ecommerce
    //   - https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#make_a_purchase_or_issue_a_refund
    // Parameters:
    //   - https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#purchase
    const detail = {
      event: "purchase",
      ecommerce: {
        transaction_id: payload.transaction_eid,
        value: payload.value,
        tax: payload.tax,
        shipping: payload.shipping,
        currency: payload.currency,
        items: iframeItems,
      },
    };
    console.log("[Rinsed] tracking.js: dispatching tracking:event", detail);
    window.dispatchEvent(new CustomEvent("tracking:event", { detail }));
    setTimeout(callback, 2000);
  } else if (google_container_id) {
    getAnalytics().then((analytics) => analytics?.track("purchase", mergedProperties));
  }

  const gtagCallback = trackingIframeEnabled ? undefined : callback;

  if (typeof gtag !== "undefined" && !serverSideGoogleAnalytics) {
    const gtagEventPayload = {
      transaction_id: payload.transaction_id,
      value: payload.value,
      currency: payload.currency || "USD",
      tax: payload.tax,
      shipping: payload.shipping,
      items: internalItems,
    };

    gtag("event", "purchase", {
      ...gtagEventPayload,
      event_callback: gtagCallback,
    });
  } else if (gtagCallback) {
    gtagCallback();
  }
};
